// Connexion en deux étapes : identifiant et mot de passe, puis code du second facteur.
import type { EtapeMfa } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Formulaire, IndicateurEnvironnement } from '@cc/ui';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ErreurApi, messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import styles from './acces.module.css';
import { BoutonCodePresentation, ComptesPresentation } from './ComptesPresentation';
import { LogoComplet } from './Logo';

function messageConnexion(e: unknown): string {
  if (e instanceof ErreurApi) {
    if (e.code === 'identifiants_invalides') return 'Identifiant ou mot de passe incorrect.';
    if (e.code === 'trop_de_tentatives') {
      const secondes = (e.details as { reessayer_dans_secondes?: number } | undefined)?.reessayer_dans_secondes;
      return `Connexion temporairement verrouillée après plusieurs échecs${secondes ? ` : réessayez dans ${Math.ceil(secondes / 60)} minute(s)` : ''}.`;
    }
    if (e.code === 'code_invalide') return 'Code incorrect : vérifiez l\'heure de votre téléphone et saisissez le code affiché.';
    if (e.code === 'jeton_invalide') return 'Cette étape a expiré : saisissez de nouveau votre identifiant et votre mot de passe.';
  }
  return messageErreur(e);
}

export function PageConnexion() {
  const { connecter, verifierMfa, expiree, etat } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { depuis?: string } | null)?.depuis ?? '/';
  const [identifiant, setIdentifiant] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [etapeMfa, setEtapeMfa] = useState<EtapeMfa | null>(null);
  const [code, setCode] = useState('');
  const [secours, setSecours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreursChamps, setErreursChamps] = useState<{ champ: string; message: string }[]>([]);
  const [enCours, setEnCours] = useState(false);
  /** Identifiant du compte de présentation choisi par bouton (code du second facteur remplissable). */
  const [comptePresentation, setComptePresentation] = useState<string | null>(null);
  const champCode = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'Connexion - CollectivitésConnect';
  }, []);
  useEffect(() => {
    if (etat === 'connecte' && !etapeMfa) navigate(destination, { replace: true });
  }, [etat, etapeMfa, navigate, destination]);
  useEffect(() => {
    if (etapeMfa) champCode.current?.focus();
  }, [etapeMfa, secours]);

  const soumettreIdentifiants = async () => {
    const manquants = [
      !identifiant.trim() ? { champ: 'identifiant', message: 'Saisissez votre identifiant' } : null,
      !motDePasse ? { champ: 'mot-de-passe', message: 'Saisissez votre mot de passe' } : null,
    ].filter((x): x is { champ: string; message: string } => x !== null);
    setErreursChamps(manquants);
    if (manquants.length > 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      const r = await connecter(identifiant, motDePasse);
      if (r !== 'connecte') {
        setEtapeMfa(r);
        setMotDePasse('');
      } else navigate(destination, { replace: true });
    } catch (e) {
      setErreur(messageConnexion(e));
    } finally {
      setEnCours(false);
    }
  };

  const soumettreCode = async () => {
    if (!etapeMfa) return;
    const valeur = code.replace(/\s/g, '');
    if (!secours && !/^\d{6}$/.test(valeur)) {
      setErreursChamps([{ champ: 'code', message: 'Le code comporte six chiffres' }]);
      return;
    }
    if (secours && valeur.length < 8) {
      setErreursChamps([{ champ: 'code', message: 'Le code de secours comporte au moins huit caractères' }]);
      return;
    }
    setErreursChamps([]);
    setErreur(null);
    setEnCours(true);
    try {
      await verifierMfa(etapeMfa.jeton_temporaire, secours ? { code_secours: valeur } : { code: valeur });
      navigate(destination, { replace: true });
    } catch (e) {
      setErreur(messageConnexion(e));
      if (e instanceof ErreurApi && e.code === 'jeton_invalide') {
        setEtapeMfa(null);
        setCode('');
      }
    } finally {
      setEnCours(false);
    }
  };

  const erreurChamp = (champ: string) => erreursChamps.find((e) => e.champ === champ)?.message ?? null;

  return (
    <div className={styles.page}>
      <LogoComplet />
      <main className={styles.carte} id="contenu-principal">
        <h1>{etapeMfa ? 'Second facteur' : 'Connexion'}</h1>
        {expiree && !etapeMfa ? (
          <Bandeau type="attention" titre="Session expirée">
            Reconnectez-vous pour continuer. Les brouillons en cours sont conservés sur cet appareil.
          </Bandeau>
        ) : null}
        {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
        {!etapeMfa ? (
          <Formulaire
            libelle="Connexion"
            onSoumettre={() => void soumettreIdentifiants()}
            erreurs={erreursChamps}
            actions={
              <Bouton type="submit" chargement={enCours} libelleChargement="Vérification en cours" pleineLargeur>
                Se connecter
              </Bouton>
            }
          >
            <Champ
              id="identifiant"
              libelle="Identifiant"
              aide="Votre courriel ou votre numéro de téléphone"
              autoComplete="username"
              value={identifiant}
              onChange={(e) => {
                setIdentifiant(e.target.value);
                setComptePresentation(null);
              }}
              erreur={erreurChamp('identifiant')}
              required
            />
            <Champ
              id="mot-de-passe"
              libelle="Mot de passe"
              type="password"
              autoComplete="current-password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              erreur={erreurChamp('mot-de-passe')}
              required
            />
          </Formulaire>
        ) : (
          <Formulaire
            libelle="Second facteur"
            onSoumettre={() => void soumettreCode()}
            erreurs={erreursChamps}
            actions={
              <>
                <Bouton type="submit" chargement={enCours} libelleChargement="Vérification en cours">
                  Valider le code
                </Bouton>
                <Bouton
                  variante="discret"
                  onClick={() => {
                    setSecours((s) => !s);
                    setCode('');
                    setErreursChamps([]);
                  }}
                >
                  {secours ? 'Utiliser le code de l\'application' : 'Utiliser un code de secours'}
                </Bouton>
              </>
            }
          >
            <p className={styles.aide}>
              {secours
                ? 'Saisissez l\'un des codes de secours remis à l\'activation du second facteur. Chaque code ne sert qu\'une fois.'
                : 'Ouvrez votre application d\'authentification et saisissez le code à six chiffres affiché pour CollectivitésConnect.'}
            </p>
            <Champ
              ref={champCode}
              id="code"
              libelle={secours ? 'Code de secours' : 'Code à six chiffres'}
              inputMode={secours ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              erreur={erreurChamp('code')}
              maxLength={secours ? 32 : 6}
              required
            />
            {comptePresentation && !secours ? <BoutonCodePresentation identifiant={comptePresentation} onCode={(c) => setCode(c)} /> : null}
          </Formulaire>
        )}
        {!etapeMfa ? (
          <ComptesPresentation
            desactive={enCours}
            onChoisir={(id, mdp) => {
              setIdentifiant(id);
              setMotDePasse(mdp);
              setComptePresentation(id);
              setErreur(null);
              setErreursChamps([]);
            }}
          />
        ) : null}
      </main>
      <p className={styles.pied}>
        <IndicateurEnvironnement environnement={import.meta.env.VITE_CC_ENVIRONNEMENT} />
      </p>
    </div>
  );
}
